import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Alert, Spinner, Card } from './shared';

const TRIGGERS = ['before_model', 'after_model', 'before_tool', 'after_tool', 'before_agent'];
const INJECT_METHODS = ['system_message', 'tool_message', 'state_update'];
const CONDITION_TYPES = [
    { type: 'turn_interval', label: 'Turn Interval', fields: ['interval'] },
    { type: 'first_turn', label: 'First Turn', fields: [] },
    { type: 'state_not_empty', label: 'State Not Empty', fields: ['key'] },
    { type: 'state_empty', label: 'State Empty', fields: ['key'] },
    { type: 'tool_not_used', label: 'Tool Not Used', fields: ['tool_name', 'lookback'] },
    { type: 'tool_used', label: 'Tool Used', fields: ['tool_name', 'lookback'] },
    { type: 'has_memory_writes', label: 'Has Memory Writes', fields: ['lookback'] },
    { type: 'no_memory_writes', label: 'No Memory Writes', fields: ['lookback'] },
    { type: 'tool_name', label: 'Tool Name Match', fields: ['name'] },
];

const DEFAULT_FORM = {
    id: '',
    name: '',
    description: '',
    trigger: 'before_model',
    inject_as: 'system_message',
    template: '',
    wrap_tag: 'system-reminder',
    priority: 50,
    group: '',
    conditions: [],
};

export default function AdminReminderForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = id && id !== 'new';

    const [form, setForm] = useState({ ...DEFAULT_FORM });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [variables, setVariables] = useState([]);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        loadVariables();
        if (isEdit) loadReminder();
    }, [id]);

    const loadVariables = async () => {
        try {
            const res = await adminApi.listReminderVariables();
            setVariables(res.variables || []);
        } catch {}
    };

    const loadReminder = async () => {
        try {
            setLoading(true);
            const data = await adminApi.getReminder(id);
            setForm({
                id: data.id,
                name: data.name,
                description: data.description || '',
                trigger: data.trigger,
                inject_as: data.inject_as,
                template: data.template,
                wrap_tag: data.wrap_tag ?? '',
                priority: data.priority,
                group: data.group || '',
                conditions: data.conditions || [],
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const payload = {
                ...form,
                wrap_tag: form.wrap_tag || null,
                group: form.group || null,
            };
            if (isEdit) {
                await adminApi.updateReminder(id, payload);
            } else {
                await adminApi.createReminder(payload);
            }
            navigate('/admin/reminders');
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const addCondition = (type) => {
        const condType = CONDITION_TYPES.find(c => c.type === type);
        if (!condType) return;
        const newCond = { type };
        if (condType.fields.includes('interval')) newCond.interval = 10;
        if (condType.fields.includes('key')) newCond.key = '';
        if (condType.fields.includes('tool_name')) newCond.tool_name = '';
        if (condType.fields.includes('name')) newCond.name = '';
        if (condType.fields.includes('lookback')) newCond.lookback = 10;
        setForm(f => ({ ...f, conditions: [...f.conditions, newCond] }));
    };

    const updateCondition = (index, field, value) => {
        setForm(f => ({
            ...f,
            conditions: f.conditions.map((c, i) =>
                i === index ? { ...c, [field]: field === 'interval' || field === 'lookback' ? parseInt(value) || 0 : value } : c
            ),
        }));
    };

    const removeCondition = (index) => {
        setForm(f => ({ ...f, conditions: f.conditions.filter((_, i) => i !== index) }));
    };

    const handlePreview = async () => {
        if (!isEdit) return;
        try {
            const res = await adminApi.previewReminder(id, {});
            setPreview(res.rendered);
        } catch (err) {
            setError(err.message);
        }
    };

    const insertVariable = (name) => {
        setForm(f => ({ ...f, template: f.template + `{${name}}` }));
    };

    if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {isEdit ? `Edit Reminder: ${id}` : 'New Reminder'}
                </h2>
                <div className="flex gap-2">
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/reminders')}>Cancel</Button>
                    <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
                </div>
            </div>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

            <Card header="Basic">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ID</label>
                        <input
                            className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white disabled:opacity-50"
                            value={form.id}
                            onChange={e => setForm(f => ({ ...f, id: e.target.value }))}
                            disabled={isEdit}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                        <input
                            className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            value={form.name}
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Group</label>
                        <input
                            className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            value={form.group}
                            onChange={e => setForm(f => ({ ...f, group: e.target.value }))}
                            placeholder="e.g. memory, skills, todo"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                        <input
                            type="number"
                            className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            value={form.priority}
                            onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 0 }))}
                            min={0}
                            max={1000}
                        />
                    </div>
                </div>
                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <input
                        className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        value={form.description}
                        onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    />
                </div>
            </Card>

            <Card header="Trigger & Injection">
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Trigger</label>
                        <select
                            className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            value={form.trigger}
                            onChange={e => setForm(f => ({ ...f, trigger: e.target.value }))}
                        >
                            {TRIGGERS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Inject As</label>
                        <select
                            className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            value={form.inject_as}
                            onChange={e => setForm(f => ({ ...f, inject_as: e.target.value }))}
                        >
                            {INJECT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Wrap Tag</label>
                        <input
                            className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            value={form.wrap_tag}
                            onChange={e => setForm(f => ({ ...f, wrap_tag: e.target.value }))}
                            placeholder="system-reminder (empty = no wrap)"
                        />
                    </div>
                </div>
            </Card>

            <Card header="Conditions">
                <div className="space-y-3">
                    {form.conditions.map((cond, idx) => {
                        const condType = CONDITION_TYPES.find(c => c.type === cond.type);
                        return (
                            <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-750 rounded-lg">
                                <span className="text-sm font-medium text-blue-600 dark:text-blue-400 min-w-[120px]">{cond.type}</span>
                                {condType?.fields.includes('interval') && (
                                    <label className="flex items-center gap-1 text-sm">
                                        interval:
                                        <input type="number" className="w-16 px-2 py-1 border rounded text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                            value={cond.interval || ''} onChange={e => updateCondition(idx, 'interval', e.target.value)} />
                                    </label>
                                )}
                                {condType?.fields.includes('key') && (
                                    <label className="flex items-center gap-1 text-sm">
                                        key:
                                        <input className="w-40 px-2 py-1 border rounded text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                            value={cond.key || ''} onChange={e => updateCondition(idx, 'key', e.target.value)} />
                                    </label>
                                )}
                                {condType?.fields.includes('tool_name') && (
                                    <label className="flex items-center gap-1 text-sm">
                                        tool:
                                        <input className="w-32 px-2 py-1 border rounded text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                            value={cond.tool_name || ''} onChange={e => updateCondition(idx, 'tool_name', e.target.value)} />
                                    </label>
                                )}
                                {condType?.fields.includes('name') && (
                                    <label className="flex items-center gap-1 text-sm">
                                        name:
                                        <input className="w-32 px-2 py-1 border rounded text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                            value={cond.name || ''} onChange={e => updateCondition(idx, 'name', e.target.value)} />
                                    </label>
                                )}
                                {condType?.fields.includes('lookback') && (
                                    <label className="flex items-center gap-1 text-sm">
                                        lookback:
                                        <input type="number" className="w-16 px-2 py-1 border rounded text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                            value={cond.lookback || ''} onChange={e => updateCondition(idx, 'lookback', e.target.value)} />
                                    </label>
                                )}
                                <button type="button" onClick={() => removeCondition(idx)}
                                    className="ml-auto text-red-500 hover:text-red-700 text-sm">Remove</button>
                            </div>
                        );
                    })}
                    <div>
                        <select
                            className="px-3 py-1.5 text-sm border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            defaultValue=""
                            onChange={e => { if (e.target.value) { addCondition(e.target.value); e.target.value = ''; } }}
                        >
                            <option value="">+ Add condition...</option>
                            {CONDITION_TYPES.map(c => <option key={c.type} value={c.type}>{c.label}</option>)}
                        </select>
                    </div>
                </div>
            </Card>

            <Card header="Template">
                <div className="mb-2 flex flex-wrap gap-1">
                    {variables.map(v => (
                        <button
                            key={v.name}
                            type="button"
                            onClick={() => insertVariable(v.name)}
                            className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50"
                            title={v.description}
                        >
                            {`{${v.name}}`}
                        </button>
                    ))}
                </div>
                <textarea
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                    rows={10}
                    value={form.template}
                    onChange={e => setForm(f => ({ ...f, template: e.target.value }))}
                    placeholder="Template text with {variable} placeholders..."
                />
                {isEdit && (
                    <div className="mt-3">
                        <Button type="button" variant="secondary" size="sm" onClick={handlePreview}>Preview</Button>
                        {preview && (
                            <pre className="mt-2 p-3 bg-gray-50 dark:bg-gray-750 rounded-lg text-xs font-mono whitespace-pre-wrap text-gray-700 dark:text-gray-300 max-h-48 overflow-auto">
                                {preview}
                            </pre>
                        )}
                    </div>
                )}
            </Card>
        </form>
    );
}
